import { Button, Form, Input } from 'antd';
import { get } from 'lodash';
import React, { useState } from 'react';
import styled from 'styled-components';
import { setAuthHeaders } from '@tools/ui/api';
import { semanticTheme } from '@tools/ui/dime';
import { permissionStore, userStore } from '@tools/ui-stores';
import { openToast } from '@tools/ui-utils/toast.util';
import { bypassLogin } from '../../api/auth.api';

const BypassContainer = styled.div`
  margin-top: ${semanticTheme.dime.spacing.l};
  padding: ${semanticTheme.dime.spacing.l};
  border: 1px solid ${semanticTheme.dime.colors.border.static};
  border-radius: ${semanticTheme.dime.radius.m};
  background-color: ${semanticTheme.dime.colors.background.secondary};
`;

const BypassTitle = styled.div`
  font-weight: ${semanticTheme.dime.fonts.weights.semiBold};
  font-size: ${semanticTheme.dime.fonts.sizes.sm};
  color: ${semanticTheme.dime.colors.text.primary};
  margin-bottom: ${semanticTheme.dime.spacing.l};
  text-align: center;
`;

const BypassForm = styled(Form)`
  .ant-form-item {
    margin-bottom: ${semanticTheme.dime.spacing.s};
  }
`;

const BypassButton = styled(Button)`
  width: 100%;
  margin-top: ${semanticTheme.dime.spacing.l};
  height: ${semanticTheme.dime.spacing['3xl']};
`;

interface BypassLoginFormProps {
  onSuccess?: () => void;
}

interface BypassFormValues {
  username: string;
  password: string;
}

export const BypassLoginForm: React.FC<BypassLoginFormProps> = ({ onSuccess }) => {
  const { setUserLoggedIn } = userStore;
  const [form] = Form.useForm<BypassFormValues>();
  const [loading, setLoading] = useState(false);

  const handleBypassLogin = async (values: unknown) => {
    const formValues = values as BypassFormValues;
    try {
      setLoading(true);
      permissionStore.toggleLoading(true);

      const loggedInUser = await bypassLogin(formValues.username, formValues.password);
      setAuthHeaders(loggedInUser?.token);
      userStore.setUser(loggedInUser);
      setUserLoggedIn(true);

      onSuccess?.();
    } catch (err) {
      const msg = get(err, 'response.data.message', (err as Error).message);
      permissionStore.toggleLoading(false);
      setLoading(false);
      openToast({
        message: 'Error',
        description: msg,
        type: 'error',
      });
    }
  };

  return (
    <BypassContainer>
      <BypassTitle>Bypass Login</BypassTitle>
      <BypassForm form={form} layout="vertical" onFinish={handleBypassLogin} size="small">
        <Form.Item
          name="username"
          label="Username"
          rules={[
            {
              required: true,
              message: 'Username is required',
            },
            {
              type: 'email',
              message: 'Username must be a valid email',
            },
          ]}
        >
          <Input placeholder="Enter username" />
        </Form.Item>
        <Form.Item
          name="password"
          label="Password"
          rules={[
            {
              required: true,
              message: 'Password is required',
            },
          ]}
        >
          <Input.Password placeholder="Enter password" autoComplete="off" />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <BypassButton type="primary" htmlType="submit" loading={loading}>
            Login
          </BypassButton>
        </Form.Item>
      </BypassForm>
    </BypassContainer>
  );
};
